import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  IconButton,
  Chip,
  Skeleton,
  Button,
} from '@mui/material';
import React, { useEffect, useState, startTransition, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, CalendarTodayOutlined, DeleteOutlined, Satellite } from '@mui/icons-material';
import clientService from 'src/clientService';
import { ProjectStatusEnum, UserRole } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar } from 'src/view/components/SnackbarService';
import FullPageMessage from 'src/view/components/FullPageMessage';
import ProjectDialog from 'src/view/components/ProjectDialog';
import Header from 'src/view/components/navigation/Header';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import DownloadAppDialog from 'src/view/components/DownloadAppDialog';
import { InstallMobile } from '@mui/icons-material';
import './style.scss';
import CarbonCreditIcon from '@mui/icons-material/Cloud';
import BioCreditIcon from '@mui/icons-material/Nature';
import BioMonitorIcon from '@mui/icons-material/Monitor';
import { PermissionGuard } from 'src/auth/PermissionGuard';
import { formatDate } from 'src/util/date-utils';
import { Project } from 'src/api/Api';
import { SampleProjectId } from 'src/enum';

const SimplifiedProjectCard: React.FC<{
  project: Project;
  onDeleteClick: (project: Project) => void;
}> = React.memo(
  ({ project, onDeleteClick }) => {
    const navigate = useNavigate();

    const hasDates = project.project_start_date || project.project_end_date;
    const formattedStartDate = formatDate(project.project_start_date);
    const formattedEndDate = formatDate(project.project_end_date);
    const { state, dispatch } = useAppState();
    const isSampleProject = project.project_id === SampleProjectId;
    const isAdmin = state.user?.role.includes(UserRole.Admin);
    const canDelete = isAdmin || !isSampleProject;

    const handleCardClick = useCallback(() => {
      startTransition(() => navigate(`/project/${project.project_id}/overview`));
    }, [navigate, project.project_id]);

    const handleAddSiteClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        startTransition(() => navigate(`/project/${project.project_id}/add-site`));
      },
      [navigate, project.project_id],
    );

    const handleViewSitesClick = useCallback(
      async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
          const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(
            project.project_id
          );
          dispatch({
            type: 'SET_PROJECT_SITES',
            payload: response.data || [],
          });
        } catch (error) {
          console.error('Failed to pre-fetch sites from landing page:', error);
        }
        startTransition(() => {
          navigate(`/project/${project.project_id}/sites`);
        });
      },
      [navigate, project.project_id, dispatch],
    );

    const renderSiteChip = () => {
      const count = project.site_count || 0;

      if (count > 0) {
        return (
          <Chip
            label={`${count} Site${count > 1 ? 's' : ''}`}
            size="small"
            icon={<Satellite />}
            variant="outlined"
            onClick={handleViewSitesClick}
            sx={{
              mr: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        );
      }

      return (
        <Chip
          label="Add First Site"
          size="small"
          icon={<Add />}
          color="secondary"
          onClick={handleAddSiteClick}
          variant="filled"
          sx={{
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.9,
            },
          }}
        />
      );
    };

    return (
      <Card className="project-card">
        <Box sx={{ cursor: 'pointer' }} onClick={handleCardClick}>
          <CardHeader
            title={project.project_name}
            subheader={
              <Chip
                label={project.project_status}
                size="small"
                color={project.project_status === ProjectStatusEnum.Draft ? 'default' : 'warning'}
              />
            }
          />
          <CardContent>
            {project.project_type && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {project.project_type}
              </Typography>
            )}

            {hasDates && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 1 }}>
                <CalendarTodayOutlined sx={{ mr: 1, fontSize: '1rem' }} />
                <Typography variant="caption">
                  {formattedStartDate || '...'} - {formattedEndDate || '...'}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {project.has_carbon_module && (
                <Chip
                  variant="outlined"
                  icon={<CarbonCreditIcon />}
                  label="Carbon"
                  size="small"
                  color="info"
                />
              )}
              {project.has_biodiversity_module && (
                <Chip
                  variant="outlined"
                  icon={<BioCreditIcon />}
                  label="Biodiversity"
                  size="small"
                  color="info"
                />
              )}
              {project.has_environmental_module && (
                <Chip
                  variant="outlined"
                  icon={<BioMonitorIcon />}
                  label="Climate"
                  size="small"
                  color="info"
                />
              )}
            </Box>
          </CardContent>
        </Box>
        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Box>{renderSiteChip()}</Box>
          <PermissionGuard resourceType="project" permission="delete" resource={project}>
            {canDelete && (
              <IconButton
                aria-label="Delete this project"
                size="small"
                onClick={() => onDeleteClick(project)}
              >
                <DeleteOutlined color="error" />
              </IconButton>
            )}
          </PermissionGuard>
        </CardActions>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.project.project_id === nextProps.project.project_id &&
      prevProps.project.project_name === nextProps.project.project_name &&
      prevProps.project.project_status === nextProps.project.project_status &&
      prevProps.project.site_count === nextProps.project.site_count
    );
  },
);

const CreateProjectCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Card className="project-card create-project-card" onClick={onClick}>
      <CardContent>
        <Add sx={{ fontSize: 48 }} />
        <Typography variant="h6">Create New Project</Typography>
      </CardContent>
    </Card>
  );
};

const ProjectCardSkeleton: React.FC = () => (
  <Card className="project-card">
    <CardHeader
      title={<Skeleton variant="text" width="60%" />}
      subheader={<Skeleton variant="circular" width={60} height={24} />}
    />
    <CardContent>
      <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
        <Skeleton variant="text" width="50%" />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Skeleton variant="rounded" width={70} height={24} />
        <Skeleton variant="rounded" width={90} height={24} />
        <Skeleton variant="rounded" width={70} height={24} />
      </Box>
    </CardContent>
    <CardActions>
      <Skeleton variant="rounded" width={100} height={32} sx={{ ml: 1 }} />
    </CardActions>
  </Card>
);

const LandingPage: React.FC = () => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadAppDialogOpen, setIsDownloadAppDialogOpen] = useState(false);

  useEffect(() => {
    // Show popup on initial landing
    const timer = setTimeout(() => {
      setIsDownloadAppDialogOpen(true);
    }, 1500); // Slight delay for better UX
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await clientService.api.readProjectsApiV1ProjectsGet();
        const projects = response.data || [];

        startTransition(() => {
          dispatch({
            type: 'SET_PROJECT_LIST',
            payload: projects,
          });
          setIsLoading(false);
        });
      } catch (apiError: any) {
        const errorMessage = apiError.response?.data?.detail || 'Failed to load projects.';
        setError(errorMessage);
        showSnackbar({ message: errorMessage, type: snackbarType.ERROR });
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [dispatch]);

  const handleDialogOpen = useCallback(() => setIsDialogOpen(true), []);
  const handleDialogClose = useCallback(() => setIsDialogOpen(false), []);

  const handleDeleteProjectClick = useCallback((project: Project) => {
    setProjectToDelete(project);
    setIsConfirmDeleteDialogOpen(true);
  }, []);

  const handleConfirmProjectDelete = useCallback(async () => {
    if (!projectToDelete) return;

    try {
      setIsDeleting(true);
      await clientService.api.deleteProjectApiV1ProjectsProjectIdDelete(projectToDelete.project_id);
      showSnackbar({ message: 'Project deleted successfully!' });

      startTransition(() => {
        const updatedProjects = state.projects.filter(
          (p) => p.project_id !== projectToDelete.project_id,
        );
        dispatch({ type: 'SET_PROJECT_LIST', payload: updatedProjects });
        setIsConfirmDeleteDialogOpen(false);
        setProjectToDelete(null);
        setIsDeleting(false);
      });
    } catch (error: any) {
      console.error('Error deleting project:', error);
      const message =
        error.error?.detail || error.message || 'Unknown error during project deletion.';
      showSnackbar({
        message: `Error deleting project: ${message}`,
        type: snackbarType.ERROR,
      });
      setIsDeleting(false);
    }
  }, [projectToDelete, state.projects, dispatch, showSnackbar]);

  const handleCancelProjectDelete = useCallback(() => {
    setIsConfirmDeleteDialogOpen(false);
    setProjectToDelete(null);
  }, []);

  // Check if user can create projects
  const canCreateProject = !state.user?.role.includes(UserRole.Employee);

  // Show skeleton loaders for initial project load
  if (isLoading) {
    return (
      <Box className="landing-page-container">
        <Header />
        <Box sx={{ paddingTop: '73px' }}>
          <Box className="landing-page-banner">
            <Container maxWidth="md">
              <Typography variant="h2" component="h1" gutterBottom>
                Welcome to Darukaa Earth
              </Typography>
              <Typography variant="h5" component="p">
                Your central hub for managing Carbon, Biodiversity, and Climate projects.
              </Typography>
            </Container>
          </Box>

          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Grid container spacing={4}>
              {/* Show 6 skeleton cards while loading */}
              {[...Array(6)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                  <ProjectCardSkeleton />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    );
  }

  if (error) {
    return <FullPageMessage headline="Error" subHeadline={error} />;
  }

  return (
    <Box className="landing-page-container">
      <Header />
      <Box sx={{ paddingTop: '73px' }}>
        <Box className="landing-page-banner">
          <Container maxWidth="md">
            <Typography variant="h2" component="h1" gutterBottom>
              Welcome to Darukaa Earth
            </Typography>
            <Typography variant="h5" component="p" gutterBottom>
              Your central hub for managing Carbon, Biodiversity, and Climate projects.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<InstallMobile />}
                onClick={() => setIsDownloadAppDialogOpen(true)}
                sx={{
                  backgroundColor: '#ffb400',
                  color: '#151E15',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '30px',
                  '&:hover': {
                    backgroundColor: '#e6a200',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                Take a look at our new app
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            {/* Create Project Card */}
            {canCreateProject && (
              <Grid item xs={12} sm={6} md={4}>
                <CreateProjectCard onClick={handleDialogOpen} />
              </Grid>
            )}

            {/* Project Cards */}
            {state.projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.project_id}>
                <SimplifiedProjectCard project={project} onDeleteClick={handleDeleteProjectClick} />
              </Grid>
            ))}

            {/* Empty state */}
            {state.projects.length === 0 && !canCreateProject && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No projects available
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>

        <ProjectDialog open={isDialogOpen} onClose={handleDialogClose} />
        <DownloadAppDialog 
          open={isDownloadAppDialogOpen} 
          onClose={() => setIsDownloadAppDialogOpen(false)} 
        />
        {projectToDelete && (
          <ConfirmationDialog
            open={isConfirmDeleteDialogOpen}
            title="Confirm Project Deletion"
            content={`Are you sure you want to delete the project "${projectToDelete.project_name}"? This will also delete all associated sites and this action cannot be undone.`}
            confirmButtonText="Delete Project"
            cancelButtonText="Cancel"
            loading={isDeleting}
            onConfirm={handleConfirmProjectDelete}
            onCancel={handleCancelProjectDelete}
          />
        )}
      </Box>
    </Box>
  );
};

export default LandingPage;